import type { ComponentProps } from "react";
import type Link from "next/link";
import type { Route } from "nextjs-routes";

type LinkHref = ComponentProps<typeof Link>["href"];
type RouterQuery = Record<string, string | string[] | undefined>;

export type BuilderDomain = "library" | "workflows" | "paths";

const STORAGE_KEYS: Record<BuilderDomain, string> = {
  library: "opus:last-library-builder-back-href",
  workflows: "opus:last-workflows-builder-back-href",
  paths: "opus:last-paths-builder-back-href",
};

export function getStorageKey(domain: BuilderDomain): string {
  return STORAGE_KEYS[domain];
}

// Which top-level "domain" a path belongs to — used so the builder back-arrow
// remembers separate last-list-view per domain (e.g. coming back from a
// workflow builder lands on /workflows, not whatever library page was last
// visited).
export function getBuilderDomain(pathname: string): BuilderDomain | null {
  if (
    pathname.startsWith("/library/library-item/") ||
    pathname.startsWith("/library/folders/") ||
    pathname === "/library" ||
    pathname === "/library/forms"
  ) {
    return "library";
  }
  if (pathname === "/workflows" || pathname === "/workflows/[id]") {
    return "workflows";
  }
  if (pathname === "/paths" || pathname === "/paths/[id]") {
    return "paths";
  }
  return null;
}

// Table state PaginatedTable keeps in unscoped URL params: column picks, sort,
// and search. A list and the item opened inline over it share one URL, so
// these must not cross between them — the item's tables would adopt the
// list's picks (a course's assignments table keeps only "status", the one
// column name it shares with the library list) and vice versa.
const UNSCOPED_TABLE_STATE_QUERY_KEYS = new Set(["columns", "sort", "q"]);

export function withoutUnscopedTableState(
  query: RouterQuery,
): Record<string, string | string[]> {
  const preservedQuery: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(query)) {
    if (UNSCOPED_TABLE_STATE_QUERY_KEYS.has(key) || value == null) {
      continue;
    }
    preservedQuery[key] = value;
  }
  return preservedQuery;
}

// Opens a library item inline over the list at `pathname`, keeping the list's
// filters (e.g. ?type=courses) so the back href can rebuild the list view.
export function getInlineLibraryItemHref(
  pathname: "/library" | "/library/forms",
  query: RouterQuery,
  libraryItemId: string,
): Route {
  return {
    pathname,
    query: { ...withoutUnscopedTableState(query), libraryItemId },
  };
}

// Every route that mounts the library item detail page, whether full-page or
// as the list's right-hand edit pane.
export function isLibraryItemBuilderView(
  pathname: string,
  query: RouterQuery,
): boolean {
  // Dynamic route — Next won't route to it without the id segment, so we
  // don't gate on `query.id` (doing so causes a hydration flash where the
  // page briefly renders as a list view before query params hydrate).
  if (pathname === "/library/library-item/[id]") {
    return true;
  }
  // /library and /library/forms ARE list views by default; only the
  // right-pane edit mode (libraryItemId query param) counts as a builder.
  return (
    (pathname === "/library" || pathname === "/library/forms") &&
    Boolean(query.libraryItemId)
  );
}

export function isBuilderView(pathname: string, query: RouterQuery): boolean {
  if (isLibraryItemBuilderView(pathname, query)) {
    return true;
  }
  return pathname === "/workflows/[id]" || pathname === "/paths/[id]";
}

// A back destination INSIDE the current builder, when it has a sub-view
// open — e.g. a workflow run's detail pops back to the runs list. Takes
// precedence over the remembered list view: back should unwind the builder
// before leaving it.
export function getBuilderInternalBackHref(
  pathname: string,
  query: RouterQuery,
): LinkHref | null {
  if (
    (pathname === "/workflows/[id]" || pathname === "/paths/[id]") &&
    query.run &&
    typeof query.id === "string"
  ) {
    // Keep the rest of the query (date filters, search) — only the run
    // sub-view closes.
    const preservedQuery: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(query)) {
      if (key === "run" || value == null) {
        continue;
      }
      preservedQuery[key] = value;
    }
    return {
      pathname,
      query: { ...preservedQuery, tab: "runs" },
    };
  }
  return null;
}

export function getBuilderBackHref(
  pathname: string,
  query: RouterQuery,
): LinkHref | null {
  if (pathname.startsWith("/library/library-item/")) {
    return "/library";
  }
  if (pathname === "/workflows/[id]") {
    return "/workflows";
  }
  if (pathname === "/paths/[id]") {
    return "/paths";
  }

  const libraryItemId = query.libraryItemId;
  if (!libraryItemId) {
    return null;
  }

  // The item's own tables may have written unscoped table state; it belongs
  // to the item, not to the list being rebuilt.
  const preservedQuery = withoutUnscopedTableState(query);
  delete preservedQuery.libraryItemId;

  if (pathname === "/library") {
    return { pathname: "/library", query: preservedQuery };
  }
  if (pathname === "/library/forms") {
    return { pathname: "/library/forms", query: preservedQuery };
  }

  return null;
}

// Where to send the user after an action that removes the current builder
// item from under them (e.g. archive). Mirrors the builder back button:
// prefer the remembered last list view for this domain (a clean list asPath,
// tracked by PageContentHeaderZone), and only fall back to the derived back
// href — which preserves the current query params — when nothing is
// remembered (e.g. a deep link straight into the builder).
export function getBuilderExitHref(
  pathname: string,
  query: RouterQuery,
): LinkHref {
  const domain = getBuilderDomain(pathname);
  if (domain && typeof window !== "undefined") {
    const storedBackHref = window.sessionStorage.getItem(getStorageKey(domain));
    if (storedBackHref) {
      // Stored as router.asPath by PageContentHeaderZone — a valid app URL,
      // but sessionStorage only hands back `string` (same cast as the back
      // button).
      return storedBackHref as LinkHref;
    }
  }
  return getBuilderBackHref(pathname, query) ?? "/library";
}

export function isTrackableListView(
  pathname: string,
  query: RouterQuery,
): boolean {
  if (pathname === "/workflows" || pathname === "/paths") return true;
  if (pathname.startsWith("/library/folders/")) return true;
  return (
    (pathname === "/library" || pathname === "/library/forms") &&
    !query.libraryItemId
  );
}
